import React, { useState, useEffect } from 'react';
import {
  getAllCurrenciesList,
  getSupportedChains,
  getOrCreateDepositAddress,
} from '../../services/funds.service';
import { useQrDepositeModal } from '../../recoil/hooks/useModalState';
import { SupportedChain } from '../../types/funds.types';

// Mock user details hook - replace with actual implementation in sister project
const useUserDetails = () => {
  const [user, setUser] = useState<any>({ isKycVerified: false }); // Default to false for testing
  return [user, setUser] as const;
};

// Mock navigation - replace with actual navigation in sister project
const useNavigate = () => {
  return (path: string) => {
    console.log('Navigating to:', path);
    // Implement actual navigation in sister project
  };
};

// Mock routes config - replace with actual routes in sister project
const ROUTES_CONFIG = {
  identification: { path: '/identification' },
};

export default function QrDepositeModalTailwind() {
  const [user] = useUserDetails();
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [chains, setChains] = useState<SupportedChain[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const [selectedChain, setSelectedChain] = useState<string>('');
  const [depositAddress, setDepositAddress] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<React.ReactNode>(null);
  const [isOpen, setIsOpen] = useQrDepositeModal();
  const navigate = useNavigate();

  useEffect(() => {
    loadCurrencies();
    loadChains();
  }, []);

  const loadCurrencies = async () => {
    try {
      const currencyList = await getAllCurrenciesList();
      setCurrencies(currencyList);
      if (currencyList.length > 0) {
        setSelectedCurrency(currencyList[0]);
      }
    } catch (err) {
      console.error('Failed to load currencies:', err);
      setError('Failed to load currencies');
    }
  };

  const loadChains = async () => {
    try {
      const chainList = await getSupportedChains();
      setChains(chainList);
      if (chainList.length > 0) {
        setSelectedChain(chainList[0].chain);
      }
    } catch (err) {
      console.error('Failed to load chains:', err);
      setError('Failed to load chains');
    }
  };

  const handleGetDepositWallet = async () => {
    // if (!user?.isKycVerified) {
    //   setLoading(true);
    //   setTimeout(() => {
    //     setError(<>Please complete <strong style={{ cursor: 'pointer', textDecoration: 'underline' }}
    //       onClick={() => {
    //         navigate(ROUTES_CONFIG.identification.path);
    //         // Close the modal when navigating to KYC verification
    //         closeModal();
    //       }}
    //     >KYC verification</strong> to deposit funds.</>);
    //     setLoading(false);
    //     // Also close the modal immediately when showing the error
    //     closeModal();
    //   }, 2000);
    //   return;
    // }

    if (!selectedCurrency || !selectedChain) {
      setError('Please select both currency and chain');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await getOrCreateDepositAddress(
        selectedCurrency,
        selectedChain
      );
      console.log({ response });
      if (response.address) {
        setDepositAddress(response.address);
        // Generate QR code URL (you can use a QR code service or library)
        setQrCodeUrl(
          `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
            response.address
          )}`
        );
      } else {
        setError(
          'Unable to get deposit address, please try another chain or currency.'
        );
      }
    } catch (err) {
      console.error('Failed to get deposit address:', err);
      setError('Failed to get deposit address');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  return (
    <div className=" z-10">
      {/* Modal Overlay */}
      {isOpen && (
        <div className="">
          <div className="bg-white rounded dark:!bg-[#121212] max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex p-4 items-center justify-between border-b border-gray-200 z-10 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Deposit Crypto
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 cursor-pointer
                dark:text-gray-300 dark:hover:text-gray-100 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="py-6 px-4 space-y-4">
              {/* Currency Selection */}
              <div>
                <label className="block text-f12 font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Select Currency
                </label>
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  disabled={loading}
                  className="w-full text-f14 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50"
                >
                  <option className="bg-pink" value="">
                    Choose Currency
                  </option>
                  {Object.values(currencies)?.map((currency, idx) => (
                    <option key={idx} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chain Selection */}
              <div>
                <label className="block text-f12 font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Select Chain
                </label>
                <select
                  value={selectedChain}
                  onChange={(e) => setSelectedChain(e.target.value)}
                  disabled={loading}
                  className="w-full text-f14 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50"
                >
                  <option value="">Choose Chain</option>
                  {Object.values(chains)?.map((chain, idx) => (
                    <option key={idx} value={chain.chain}>
                      {chain.name} ({chain.chain})
                    </option>
                  ))}
                </select>
              </div>

              {/* QR Code Display */}
              <div className="text-center py-4">
                {qrCodeUrl ? (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <img
                        src={qrCodeUrl}
                        alt="Deposit Address QR Code"
                        className="w-48 h-48 rounded-lg shadow-md"
                      />
                    </div>
                    <div>
                      <p className="text-f12 font-bold mt-2 text-gray-600 dark:text-gray-400 mb-2">
                        Deposit Address:
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <code className="text-f12 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded break-all max-w-xs">
                          {depositAddress}
                        </code>

                        <button
                          onClick={() => copyToClipboard(depositAddress)}
                          className="p-2 scale-125 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                          title="Copy address"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <svg
                      className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2zM15 13h2v2h-2zM13 16h2v2h-2zM15 16h2v2h-2zM18 13h2v2h-2zM20 13h2v2h-2zM18 16h2v2h-2zM20 16h2v2h-2z" />
                    </svg>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      Select currency and chain, then click "Get Deposit Wallet"
                      to generate QR code
                    </p>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Get Deposit Wallet Button */}
              <button
                onClick={handleGetDepositWallet}
                // disabled={loading || !selectedCurrency || !selectedChain}
                className={`w-full py-3 px-4 border-1 dark:text-white border-[#E5E7EB] text-[#374151] hover:text-[#111827] text-f14 rounded-lg font-semibold transition-colors duration-200
                   ${
                     loading || !selectedCurrency || !selectedChain
                       ? '!bg-gray-300 cursor-not-allowed'
                       : '!bg-blue-600 hover:!bg-blue-700 '
                   }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </div>
                ) : (
                  'Get Deposit Wallet'
                )}
              </button>

              {/* Warning Message */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400 px-4 py-3 rounded-lg text-f12">
                <div className="flex text-f10 items-start gap-2">
                  <svg
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p>
                    Send only {selectedCurrency || 'selected currency'} to this
                    address. Sending any other coin may result in permanent
                    loss.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
